import { useState, useEffect } from "react";
import axios from "axios";
import "./ConstructionCostManagement.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function ConstructionCostManagement() {
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [selectedBuildingType, setSelectedBuildingType] = useState("");
  const [costs, setCosts] = useState([]);
  const [buildingTypeInfo, setBuildingTypeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCost, setNewCost] = useState({
    workName: "",
    materialCost: 0,
    laborCost: 0,
    expenseCost: 0,
    indirectCost: 0,
    notes: "",
  });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showGPTModal, setShowGPTModal] = useState(false);
  const [generatedCosts, setGeneratedCosts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");

  useEffect(() => {
    fetchBuildingTypes();
  }, []);

  useEffect(() => {
    if (selectedBuildingType) {
      fetchConstructionCosts(selectedBuildingType);
    }
  }, [selectedBuildingType]);

  const fetchBuildingTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/building-types`);
      setBuildingTypes(response.data);
      if (response.data.length > 0 && !selectedBuildingType) {
        setSelectedBuildingType(response.data[0].code);
      }
    } catch (error) {
      console.error("Error fetching building types:", error);
      alert("載入建築類型失敗。");
    } finally {
      setLoading(false);
    }
  };

  const fetchConstructionCosts = async (buildingType) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/construction-costs`,
        { params: { buildingType } }
      );
      setCosts(response.data.costs || []);
      setBuildingTypeInfo(response.data.buildingType);
    } catch (error) {
      console.error("Error fetching construction costs:", error);
      alert("載入建築單價失敗。");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/construction-costs/history`,
        { params: { limit: 20 } }
      );
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const handleEdit = (cost) => {
    setEditingId(cost.id);
    setEditForm({
      workName: cost.workName,
      materialCost: cost.materialCost,
      laborCost: cost.laborCost,
      expenseCost: cost.expenseCost,
      indirectCost: cost.indirectCost,
      notes: cost.notes || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (id) => {
    try {
      setLoading(true);
      await axios.patch(`${API_BASE_URL}/api/admin/construction-costs/${id}`, editForm);
      await fetchConstructionCosts(selectedBuildingType);
      setEditingId(null);
      setEditForm({});
      alert("修改完成。");
    } catch (error) {
      console.error("Error updating cost:", error);
      alert("修改失敗。");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("確定要刪除嗎？")) {
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/admin/construction-costs/${id}`);
      await fetchConstructionCosts(selectedBuildingType);
      alert("刪除完成。");
    } catch (error) {
      console.error("Error deleting cost:", error);
      alert("刪除失敗。");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCost = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${API_BASE_URL}/api/admin/construction-costs`,
        newCost,
        { params: { buildingType: selectedBuildingType } }
      );
      await fetchConstructionCosts(selectedBuildingType);
      setShowAddForm(false);
      setNewCost({
        workName: "",
        materialCost: 0,
        laborCost: 0,
        expenseCost: 0,
        indirectCost: 0,
        notes: "",
      });
      alert("工種已新增。");
    } catch (error) {
      console.error("Error adding cost:", error);
      alert("工種新增失敗。");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (material, labor, expense, indirect) => {
    return (
      (parseInt(material) || 0) +
      (parseInt(labor) || 0) +
      (parseInt(expense) || 0) +
      (parseInt(indirect) || 0)
    );
  };

  const formatCurrency = (value) => {
    return "NT$" + new Intl.NumberFormat("zh-TW", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const handleGenerateWithGPT = async () => {
    if (!selectedBuildingType) {
      alert("請選擇建築類型。");
      return;
    }

    try {
      setGenerating(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/building-types/${selectedBuildingType}/generate-costs`,
        { customInstructions: customInstructions || undefined }
      );

      if (response.data.success && response.data.costs) {
        console.log("response.data.costs", response.data.costs);
        setGeneratedCosts(response.data.costs);
        setShowGPTModal(true);
      } else {
        alert("AI單價生成失敗。");
      }
    } catch (error) {
      console.error("Error generating costs with GPT:", error);
      alert("AI單價生成時發生錯誤：" + (error.response?.data?.message || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateGeneratedCost = (index, field, value) => {
    const updatedCosts = [...generatedCosts];
    updatedCosts[index] = {
      ...updatedCosts[index],
      [field]: field === 'workName' || field === 'notes' ? value : parseInt(value) || 0,
    };
    
    // totalCost 자동 계산
    if (['materialCost', 'laborCost', 'expenseCost', 'indirectCost'].includes(field)) {
      updatedCosts[index].totalCost = 
        (updatedCosts[index].materialCost || 0) +
        (updatedCosts[index].laborCost || 0) +
        (updatedCosts[index].expenseCost || 0) +
        (updatedCosts[index].indirectCost || 0);
    }
    
    setGeneratedCosts(updatedCosts);
  };

  const handleApplyGeneratedCosts = async () => {
    if (!window.confirm("確定要用生成的單價修改全部工程單價嗎？")) {
      return;
    }

    try {
      setLoading(true);
      
      // totalCost 제거 (서버에서 자동 계산)
      const costsWithoutTotal = generatedCosts.map(cost => ({
        workOrder: cost.workOrder,
        workName: cost.workName,
        materialCost: cost.materialCost,
        laborCost: cost.laborCost,
        expenseCost: cost.expenseCost,
        indirectCost: cost.indirectCost,
        notes: cost.notes || undefined,
      }));
      
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/building-types/${selectedBuildingType}/construction-costs/batch`,
        { costs: costsWithoutTotal }
      );

      if (response.data.success) {
        alert(response.data.message || "工程單價已成功修改。");
        setShowGPTModal(false);
        setGeneratedCosts([]);
        setCustomInstructions("");
        await fetchConstructionCosts(selectedBuildingType);
      } else {
        alert("工程單價修改失敗。");
      }
    } catch (error) {
      console.error("Error applying generated costs:", error);
      alert("工程單價修改時發生錯誤：" + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="construction-cost-management">
      <div className="page-header">
        <h1>建築單價管理</h1>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowHistory(!showHistory);
              if (!showHistory) {
                fetchHistory();
              }
            }}
          >
            {showHistory ? "關閉記錄" : "變更記錄"}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="history-panel">
          <h3>變更記錄</h3>
          <div className="history-list">
            {history.length === 0 ? (
              <p>沒有變更記錄。</p>
            ) : (
              history.map((item) => (
                <div key={item.id} className="history-item">
                  <div className="history-header">
                    <span className="history-type">{item.buildingType}</span>
                    <span className="history-date">
                      {new Date(item.appliedAt).toLocaleString("ko-KR")}
                    </span>
                  </div>
                  <div className="history-details">
                    <span>變更件數：{item.changesCount}</span>
                    <span>應用者：{item.appliedBy}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="building-type-selector">
        <label>建築類型選擇：</label>
        <select
          value={selectedBuildingType}
          onChange={(e) => setSelectedBuildingType(e.target.value)}
          className="select-input"
        >
          {buildingTypes.map((type) => (
            <option key={type.code} value={type.code}>
              {type.name} ({type.code})
            </option>
          ))}
        </select>
      </div>

      {buildingTypeInfo && (
        <div className="building-type-info">
          <h3>{buildingTypeInfo.name}</h3>
          <p>{buildingTypeInfo.description || "無說明"}</p>
          <p>
            <strong>適用用途區域：</strong>{" "}
            {buildingTypeInfo.applicableZoning?.join(", ") || "無"}
          </p>
        </div>
      )}

      <div className="costs-section">
        <div className="section-header">
          <h2>工種別單價列表</h2>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="btn btn-primary"
              onClick={handleGenerateWithGPT}
              disabled={!selectedBuildingType || generating}
            >
              {generating ? "生成中..." : "AI生成單價"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? "取消" : "+ 新增工種"}
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="add-form">
            <h3>新增工種</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>工種名稱</label>
                <input
                  type="text"
                  value={newCost.workName}
                  onChange={(e) =>
                    setNewCost({ ...newCost, workName: e.target.value })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>材料費 (NT$/㎡)</label>
                <input
                  type="number"
                  value={newCost.materialCost}
                  onChange={(e) =>
                    setNewCost({
                      ...newCost,
                      materialCost: parseInt(e.target.value) || 0,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>人工費 (NT$/㎡)</label>
                <input
                  type="number"
                  value={newCost.laborCost}
                  onChange={(e) =>
                    setNewCost({
                      ...newCost,
                      laborCost: parseInt(e.target.value) || 0,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>經費 (NT$/㎡)</label>
                <input
                  type="number"
                  value={newCost.expenseCost}
                  onChange={(e) =>
                    setNewCost({
                      ...newCost,
                      expenseCost: parseInt(e.target.value) || 0,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>間接工程費 (NT$/㎡)</label>
                <input
                  type="number"
                  value={newCost.indirectCost}
                  onChange={(e) =>
                    setNewCost({
                      ...newCost,
                      indirectCost: parseInt(e.target.value) || 0,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>合計 (NT$/㎡)</label>
                <input
                  type="text"
                  value={formatCurrency(
                    calculateTotal(
                      newCost.materialCost,
                      newCost.laborCost,
                      newCost.expenseCost,
                      newCost.indirectCost
                    )
                  )}
                  readOnly
                  className="form-input readonly"
                />
              </div>
              <div className="form-group full-width">
                <label>備註</label>
                <textarea
                  value={newCost.notes}
                  onChange={(e) =>
                    setNewCost({ ...newCost, notes: e.target.value })
                  }
                  className="form-input"
                  rows="2"
                />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleAddCost}>
                추가
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddForm(false)}
              >
                取消
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading">載入中...</div>
        ) : (
          <div className="costs-table-wrapper">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>順序</th>
                  <th>工種名稱</th>
                  <th>材料費</th>
                  <th>人工費</th>
                  <th>經費</th>
                  <th>間接工程費</th>
                  <th>合計</th>
                  <th>備註</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {costs.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty-state">
                      沒有註冊的工種。
                    </td>
                  </tr>
                ) : (
                  costs.map((cost) => (
                    <tr key={cost.id}>
                      {editingId === cost.id ? (
                        <>
                          <td>{cost.workOrder}</td>
                          <td>
                            <input
                              type="text"
                              value={editForm.workName}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  workName: e.target.value,
                                })
                              }
                              className="inline-input"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.materialCost}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  materialCost: parseInt(e.target.value) || 0,
                                })
                              }
                              className="inline-input"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.laborCost}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  laborCost: parseInt(e.target.value) || 0,
                                })
                              }
                              className="inline-input"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.expenseCost}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  expenseCost: parseInt(e.target.value) || 0,
                                })
                              }
                              className="inline-input"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.indirectCost}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  indirectCost: parseInt(e.target.value) || 0,
                                })
                              }
                              className="inline-input"
                            />
                          </td>
                          <td className="total-cell">
                            {formatCurrency(
                              calculateTotal(
                                editForm.materialCost,
                                editForm.laborCost,
                                editForm.expenseCost,
                                editForm.indirectCost
                              )
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editForm.notes || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  notes: e.target.value,
                                })
                              }
                              className="inline-input"
                            />
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon btn-save"
                                onClick={() => handleSaveEdit(cost.id)}
                                title="儲存"
                              >
                                ✓
                              </button>
                              <button
                                className="btn-icon btn-cancel"
                                onClick={handleCancelEdit}
                                title="取消"
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{cost.workOrder}</td>
                          <td>{cost.workName}</td>
                          <td>{formatCurrency(cost.materialCost)}</td>
                          <td>{formatCurrency(cost.laborCost)}</td>
                          <td>{formatCurrency(cost.expenseCost)}</td>
                          <td>{formatCurrency(cost.indirectCost)}</td>
                          <td className="total-cell">
                            {formatCurrency(cost.totalCost)}
                          </td>
                          <td>{cost.notes || "-"}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleEdit(cost)}
                                title="修改"
                              >
                                ✎
                              </button>
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDelete(cost.id)}
                                title="刪除"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* GPT 생성 단가 확인 모달 */}
      {showGPTModal && (
        <div className="gpt-modal-overlay" onClick={() => setShowGPTModal(false)}>
          <div className="gpt-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="gpt-modal-header">
              <h2>AI生成單價確認</h2>
              <button
                className="btn-icon btn-cancel"
                onClick={() => setShowGPTModal(false)}
                title="關閉"
              >
                ✕
              </button>
            </div>
            
            <div className="gpt-modal-body">
              <div className="gpt-instructions-section" style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "600" }}>
                  額外要求（選填）：
                </label>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="例如：材料費降低10%，人工費請調整為現實價格等"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    minHeight: "60px",
                    fontFamily: "inherit",
                  }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={handleGenerateWithGPT}
                  disabled={generating}
                  style={{ marginTop: "10px" }}
                >
                  {generating ? "重新生成中..." : "重新生成"}
                </button>
              </div>

              <div className="gpt-costs-table-wrapper" style={{ maxHeight: "500px", overflowY: "auto" }}>
                <table className="costs-table">
                  <thead>
                    <tr>
                      <th>順序</th>
                      <th>工種名稱</th>
                      <th>材料費</th>
                      <th>人工費</th>
                      <th>經費</th>
                      <th>間接工程費</th>
                      <th>合計</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedCosts.map((cost, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="number"
                            value={cost.workOrder || index + 1}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'workOrder', parseInt(e.target.value) || 0)}
                            className="inline-input"
                            style={{ width: "60px" }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={cost.workName || ""}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'workName', e.target.value)}
                            className="inline-input"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={cost.materialCost || 0}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'materialCost', e.target.value)}
                            className="inline-input"
                            style={{ width: "100px" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={cost.laborCost || 0}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'laborCost', e.target.value)}
                            className="inline-input"
                            style={{ width: "100px" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={cost.expenseCost || 0}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'expenseCost', e.target.value)}
                            className="inline-input"
                            style={{ width: "100px" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={cost.indirectCost || 0}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'indirectCost', e.target.value)}
                            className="inline-input"
                            style={{ width: "100px" }}
                          />
                        </td>
                        <td className="total-cell">
                          {formatCurrency(cost.totalCost || 0)}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={cost.notes || ""}
                            onChange={(e) => handleUpdateGeneratedCost(index, 'notes', e.target.value)}
                            className="inline-input"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="gpt-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowGPTModal(false);
                  setGeneratedCosts([]);
                  setCustomInstructions("");
                }}
              >
                關閉
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyGeneratedCosts}
                disabled={loading || generatedCosts.length === 0}
              >
                {loading ? "修改中..." : "修改"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConstructionCostManagement;

